import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * ErrorBoundary
 * =============
 *
 * Catches render/lifecycle exceptions anywhere below it so a single
 * misbehaving component shows a recoverable card instead of unmounting
 * the whole React tree (which blanks the screen — "black screen" in the
 * dark theme). Without this, any uncaught throw during render — e.g. a
 * malformed LLM `plan` payload in the Chat tab, or an unexpected figure
 * shape — takes down the entire app.
 *
 * React error boundaries MUST be class components (no hooks equivalent).
 * We reset on prop `resetKey` change so navigating to another route after
 * a crash clears the error automatically.
 */
interface Props {
  children: ReactNode;
  resetKey?: string;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface it in the console for debugging; the card is the user-facing part.
    console.error("[ErrorBoundary] caught render error:", error, info);
  }

  componentDidUpdate(prev: Props) {
    // Route changed (or other reset signal) — clear the error so the new
    // view gets a fresh render attempt instead of staying wedged.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <div className="text-red-700 font-semibold mb-1">
              Something went wrong on this page
            </div>
            <p className="text-sm text-red-600/80 mb-4">
              The rest of the app is fine — you can go back, retry, or reload.
            </p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => this.setState({ error: null })}
                className="btn"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Reload
              </button>
            </div>
            {import.meta.env.DEV && (
              <pre className="mt-4 text-left text-xs text-red-700/70 bg-white/60 rounded p-2 overflow-x-auto">
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
